/**
 * SimbyAnimation — full-screen celebration overlay triggered when the
 * acacia reaches stage 4 for the first time (simby_celebration=true).
 *
 * Props:
 *   potId    {string}  ID of the pot that triggered the celebration
 *   onDone   {fn}      called after animation finishes
 */

import { useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FLOWERS = ["🌸", "🌺", "🌼", "🌻", "💐"];

// Radial positions for floating flowers (angle in deg, distance in vw)
const FLOWER_POSITIONS = [
  { angle: 0,   dist: 22 },
  { angle: 45,  dist: 20 },
  { angle: 90,  dist: 22 },
  { angle: 135, dist: 20 },
  { angle: 180, dist: 22 },
  { angle: 225, dist: 20 },
  { angle: 270, dist: 22 },
  { angle: 315, dist: 20 },
];

export default function SimbyAnimation({ potId, onDone }) {
  const calledRef = useRef(false);

  useEffect(() => {
    // Mark celebration as seen after 2.5 s
    const timer = setTimeout(async () => {
      if (!calledRef.current) {
        calledRef.current = true;
        try {
          await axios.post(
            `${BACKEND_URL}/api/gamification/mark-celebration-seen`,
            { pot_id: potId },
            { withCredentials: true }
          );
        } catch (_) {
          // ignore — worst case animation re-plays next visit
        }
        onDone?.();
      }
    }, 2600);

    return () => clearTimeout(timer);
  }, [potId, onDone]);

  return (
    <div className="simby-anim-overlay" aria-live="polite" role="dialog" aria-label="Celebração Simby">
      <style>{ANIMATION_CSS}</style>

      {/* Backdrop */}
      <div className="simby-anim-backdrop" />

      {/* Giraffe */}
      <div className="simby-anim-giraffe">🦒</div>

      {/* Floating flowers */}
      {FLOWER_POSITIONS.map((pos, i) => {
        const rad = (pos.angle * Math.PI) / 180;
        const x = Math.cos(rad) * pos.dist;
        const y = Math.sin(rad) * pos.dist;
        return (
          <div
            key={i}
            className="simby-anim-flower"
            style={{
              "--x": `${x}vw`,
              "--y": `${y}vw`,
              animationDelay: `${i * 0.08}s`,
            }}
          >
            {FLOWERS[i % FLOWERS.length]}
          </div>
        );
      })}

      {/* Message */}
      <div className="simby-anim-msg">
        <span className="simby-anim-title">Sua acácia floresceu! 🌿</span>
        <span className="simby-anim-subtitle">Você completou mais uma ilha na Simby</span>
      </div>
    </div>
  );
}

const ANIMATION_CSS = `
  .simby-anim-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    pointer-events: none;
    animation: simby-fade-in 0.3s ease forwards,
               simby-fade-out 0.4s ease 2.2s forwards;
  }

  .simby-anim-backdrop {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle, rgba(124,58,237,0.35) 0%, rgba(0,0,0,0.6) 100%);
    animation: simby-backdrop-pulse 2.5s ease;
  }

  .simby-anim-giraffe {
    position: relative;
    font-size: clamp(5rem, 12vw, 8rem);
    animation: simby-giraffe 2.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
    filter: drop-shadow(0 0 24px rgba(251,191,36,0.7));
    z-index: 1;
  }

  .simby-anim-flower {
    position: absolute;
    top: 50%;
    left: 50%;
    font-size: clamp(1.6rem, 4vw, 2.4rem);
    transform: translate(var(--x), var(--y));
    animation: simby-flower-pop 2.5s ease forwards;
    opacity: 0;
    z-index: 1;
  }

  .simby-anim-msg {
    position: relative;
    z-index: 1;
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    animation: simby-msg-slide 0.5s ease 0.4s both;
  }

  .simby-anim-title {
    font-size: clamp(1.2rem, 4vw, 1.8rem);
    font-weight: 700;
    color: #fef3c7;
    text-shadow: 0 2px 8px rgba(0,0,0,0.5);
    text-align: center;
    padding: 0 1rem;
  }

  .simby-anim-subtitle {
    font-size: clamp(0.85rem, 2.5vw, 1.1rem);
    color: #e9d5ff;
    text-shadow: 0 1px 4px rgba(0,0,0,0.5);
    text-align: center;
    padding: 0 1rem;
  }

  @keyframes simby-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  @keyframes simby-fade-out {
    from { opacity: 1; }
    to   { opacity: 0; }
  }

  @keyframes simby-backdrop-pulse {
    0%   { opacity: 0; }
    20%  { opacity: 1; }
    80%  { opacity: 1; }
    100% { opacity: 0; }
  }

  @keyframes simby-giraffe {
    0%   { transform: scale(0.5) rotate(0deg); opacity: 0; }
    30%  { transform: scale(1.4) rotate(5deg); opacity: 1; }
    55%  { transform: scale(1.2) rotate(-3deg); }
    75%  { transform: scale(1.35) rotate(5deg); }
    100% { transform: scale(1.3) rotate(3deg); opacity: 1; }
  }

  @keyframes simby-flower-pop {
    0%   { opacity: 0; transform: translate(var(--x), var(--y)) scale(0); }
    40%  { opacity: 1; transform: translate(calc(var(--x) * 1.1), calc(var(--y) * 1.1)) scale(1.2); }
    70%  { opacity: 1; transform: translate(var(--x), var(--y)) scale(1); }
    100% { opacity: 0.8; transform: translate(var(--x), var(--y)) scale(0.9); }
  }

  @keyframes simby-msg-slide {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;
